import { AccessLevel, Difficulty } from '@prisma/client';
import {
  Sizes,
  SubscriptionDetails,
  UserClient
} from '../schemas/types/app-types';
import { SearchParams } from '../schemas/types/search-params';
import {
  ASPECT_RATIO,
  IMAGE,
  PERCENT_HORIZONTAL,
  PERCENT_VERTICAL
} from '../constants/sizes';

// pagination
const RIGHT_BOUNDARY = 5;
const SLICE_AT = 4;

export const getRange = (pages: number[], page: number) => {
  if (pages.length > RIGHT_BOUNDARY) {
    if (pages.slice(page - SLICE_AT, page + 1).length === RIGHT_BOUNDARY) {
      return pages.slice(page - SLICE_AT, page + 1); //Индекс на 2 меньше порядкового номера страницы page; массив pages содержит страницы с 2 по total-1
    } else {
      if (page < RIGHT_BOUNDARY) {
        return pages.slice(0, RIGHT_BOUNDARY);
      } else {
        return pages.slice(-RIGHT_BOUNDARY);
      }
    }
  }
  return pages;
};

export const createPagesArray = (total: number) => {
  return Array.from({ length: total - 2 }, (_, i) => i + 2);
};

/**
 * Проверяем права пользователя на запрошенный url
 *
 * @param {string} route url, на который переходит пользователь
 * @param {SubscriptionDetails[]} accessArray   массив объектов, определяющих право доступа к разделу{ branchId: number; branchSlug: string; accessLevel: AccessLevel;};
 * @returns {Promise<boolean>}
 *
 * @throws Не выбрасывает исключения
 */
export async function checkIfIsAuthorized(
  route: string,
  accessArray: SubscriptionDetails[]
): Promise<boolean> {
  const slugs = route.split('/');
  for (const access of accessArray) {
    if (slugs.includes(access.branchSlug)) return true;
  }

  return false;
}

/**
 * Проверяет есть ли у пользователя права на просмотр контента.
 *
 * @param {string } path - Текущий APP_URLS.
 * @param {UserClient } user - Объект пользователя: { id, name, hashedVkId, showAvatar, avatar, subscriptionStatus, expiresAt, access }.
 * @param {Difficulty } difficulty - Уровень сложности задачи, определяющий доступ.
 * @returns {boolean}
 *
 * @throws Не выбрасывает исключений.
 */
export const isAuthorizedToViewContent = (
  path: string,
  user: UserClient | null,
  difficulty: Difficulty
): boolean => {
  // если пользователь отсутствует, разрешается просмотр только задач базового уровня сложности
  if (!user) {
    return difficulty === Difficulty.EASY;
  }

  const slugs = path.split('/');

  return user.access.reduce(
    (prev, curr) =>
      prev ||
      (slugs.includes(curr.branchSlug) &&
        hasAccess(difficulty, curr.accessLevel)),
    false
  );
};

const hasAccess = (difficulty: Difficulty, accessLevel: AccessLevel) => {
  switch (difficulty) {
    case Difficulty.EASY:
      return true;
    case Difficulty.MEDIUM:
      return (
        accessLevel === AccessLevel.RESTRICTED ||
        accessLevel === AccessLevel.FULL
      );
    case Difficulty.HARD:
      return accessLevel === AccessLevel.FULL;
    default:
      return false;
  }
};

/**
 * Конфигурирует маршруты в соответствии с уровнем сложности задачи.
 *
 * @param {string } baseUrl - Базовый APP_URLS.
 * @param {Difficulty } difficulty - Уровень сложности задачи, определяющий доступ.
 * @param {number } problemId - id задачи для формирования динамического маршрута.
 * @returns {string} Маршрут, на который пользователь будет перенаправлен при нажатии кнопки "Смотреть решение".
 *
 * @throws Не выбрасывает исключений.
 */
export function configPathToSolutionPage(
  baseUrl: string,
  difficulty: Difficulty,
  problemId: number
): string {
  switch (difficulty) {
    case Difficulty.EASY: {
      return `${baseUrl}/${problemId}`;
    }
    default: {
      return `${baseUrl}/premium/${problemId}`;
    }
  }
}

/**
 * Проверяет, были ли сохранены searchParams.
 *
 * @param {SearchParams } searchParams
 * @returns {boolean}
 *
 * @throws Не выбрасывает исключений.
 */
export function hasSearchParams(searchParams: SearchParams): boolean {
  const { query, levels, page } = searchParams;
  return query !== '' || (levels && levels.length > 0) || page !== '1';
}

/**
 * Определяет, находимся ли мы в продакшн
 *
 * @returns {boolean}
 *
 * @throws Не выбрасывает исключений
 */
export function isProduction(): boolean {
  return process.env.NODE_ENV === 'production';
}

/**
 * Проверяет срок действия подписки
 *
 * @param {Date} subscriptionExpiresAt
 * @returns {boolean}
 *
 * @throws Не выбрасывает исключений
 */
export function isValidSubscription(subscriptionExpiresAt: Date): boolean {
  const now = new Date();
  return subscriptionExpiresAt > now;
}

/**
 * Утилита, возвращающая объект размеров изображения в соответствии с именем файла
 *
 * @param {string} url
 * @returns {{ width: number; height: number; aspect: number}}
 *
 * @throws Не выбрасывает исключений
 */
function getSize(url: string): {
  width: number;
  height: number;
  aspect: number;
} {
  const filename = url.split('/').slice(-1)[0].split('.')[0];
  const hasSize = filename.split('-size').length > 0;

  if (hasSize) {
    const sz = filename.split('-size').pop();
    switch (sz) {
      case '1':
        return {
          width: IMAGE.wide,
          height: IMAGE.default,
          aspect: ASPECT_RATIO.wide
        };
      case '2':
        return {
          width: IMAGE.default,
          height: IMAGE.tall,
          aspect: ASPECT_RATIO.narrow
        };
      case '3':
        return {
          width: IMAGE.wide,
          height: IMAGE.tall,
          aspect: ASPECT_RATIO.square
        };
      default:
        return {
          width: IMAGE.default,
          height: IMAGE.default,
          aspect: ASPECT_RATIO.square
        };
    }
  } else
    return {
      width: IMAGE.default,
      height: IMAGE.default,
      aspect: ASPECT_RATIO.square
    };
}

/**
 * Конфигурирует размеры изображения для текста и lightBox'а
 *
 * @param {string} url
 * @returns {{width: number, height: number, aspect: number, lightboxWidths:Sizes, lightboxHeights:Sizes}}
 *
 * @throws Не выбрасывает исключений
 */
export function configLightboxSize(url: string): {
  width: number;
  height: number;
  aspect: number;
  lightboxWidths: Sizes;
  lightboxHeights: Sizes;
} {
  const size = getSize(url);

  const { width, height, aspect } = size;
  const wide = aspect > 1;
  const narrow = aspect < 1;

  const lightboxWidths = {
    initial: wide
      ? `${PERCENT_HORIZONTAL.initial}vw`
      : `${Math.round(PERCENT_VERTICAL.initial * aspect)}vw`,
    sm: wide
      ? `${PERCENT_HORIZONTAL.sm}vw`
      : `${Math.round(PERCENT_VERTICAL.sm * aspect)}vh`,
    md: wide
      ? `${PERCENT_HORIZONTAL.md}vw`
      : `${Math.round(PERCENT_VERTICAL.md * aspect)}vh`,
    lg: wide
      ? `${PERCENT_HORIZONTAL.lg}vw`
      : `${Math.round(PERCENT_VERTICAL.lg * aspect)}vh`
  };
  const lightboxHeights = {
    initial: narrow
      ? `${PERCENT_VERTICAL.initial}vw`
      : `${PERCENT_HORIZONTAL.initial / aspect}vw`,
    sm: wide
      ? `${PERCENT_HORIZONTAL.sm / aspect}vw`
      : `${PERCENT_VERTICAL.sm}vh`,
    md: wide
      ? `${PERCENT_HORIZONTAL.md / aspect}vw`
      : `${PERCENT_VERTICAL.md}vh`,
    lg: wide
      ? `${PERCENT_HORIZONTAL.lg / aspect}vw`
      : `${PERCENT_VERTICAL.lg}vh`
  };

  return {
    width,
    height,
    aspect,
    lightboxWidths,
    lightboxHeights
  };
}

export const assertNever = (value: never): never => {
  throw new Error(`Unexpected value: ${JSON.stringify(value)}`);
};
// export const assertNever = (value: string): never => {
//   throw new Error(`Unexpected value: ${value}`);
// };

// Вспомогательная функция для безопасного парсинга JSON
export function parseJsonSafe<T>(
  jsonString: string | null,
  defaultValue: T
): T {
  try {
    return jsonString ? JSON.parse(jsonString) : defaultValue;
  } catch {
    return defaultValue;
  }
}
